import { create } from 'zustand';

/** Visible mechanic count above which the calendar prefers a dense layout (#2657). */
export const MANY_MECHANICS_THRESHOLD = 8;

export const CALENDAR_MECHANIC_PREFS_STORAGE_KEY = 'staff-portal-calendar-mechanic-prefs';

export type CalendarDensity = 'comfortable' | 'compact';

export interface MechanicGroup {
  id: string;
  name: string;
  mechanicIds: string[];
}

export interface TimelineDensityMetrics {
  minRowHeight: number;
  eventSlotHeight: number;
  eventVerticalGap: number;
  sidebarWidth: number;
  avatarSize: number;
}

/**
 * Comfortable rows leave room for a two-line sidebar label. Compact is the
 * 15-mechanic density: ~36px rows with 24px bars (#2657).
 */
export const TIMELINE_DENSITY: Record<CalendarDensity, TimelineDensityMetrics> = {
  comfortable: {
    minRowHeight: 44,
    eventSlotHeight: 32,
    eventVerticalGap: 4,
    sidebarWidth: 140,
    avatarSize: 28,
  },
  compact: {
    minRowHeight: 36,
    eventSlotHeight: 32,
    eventVerticalGap: 4,
    sidebarWidth: 112,
    avatarSize: 22,
  },
};

export const DAY_COLUMN_MIN_WIDTH_DEFAULT = 100;
export const DAY_COLUMN_MIN_WIDTH_DENSE = 72;

export function newMechanicGroupId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `grp-${String(Date.now())}-${Math.random().toString(36).slice(2, 8)}`;
}

export function createMechanicGroup(name: string, mechanicIds: string[]): MechanicGroup | null {
  const trimmed = name.trim();
  if (trimmed.length === 0 || mechanicIds.length === 0) return null;
  return {
    id: newMechanicGroupId(),
    name: trimmed,
    mechanicIds: [...mechanicIds],
  };
}

export function timelineEventHeight(density: CalendarDensity): number {
  const metrics = TIMELINE_DENSITY[density];
  return metrics.eventSlotHeight - 2 * metrics.eventVerticalGap;
}

export function timelineRowHeight(overlap: number, density: CalendarDensity): number {
  const metrics = TIMELINE_DENSITY[density];
  const slots = Math.max(1, overlap);
  return Math.max(metrics.minRowHeight, metrics.eventSlotHeight * slots);
}

export function dayColumnMinWidth(visibleMechanicCount: number): number {
  return visibleMechanicCount > MANY_MECHANICS_THRESHOLD
    ? DAY_COLUMN_MIN_WIDTH_DENSE
    : DAY_COLUMN_MIN_WIDTH_DEFAULT;
}

export function shouldDefaultToTimeline(
  visibleMechanicCount: number,
  dayLayoutTouched: boolean,
): boolean {
  return !dayLayoutTouched && visibleMechanicCount > MANY_MECHANICS_THRESHOLD;
}

export function resolveCalendarDensity(
  stored: CalendarDensity | null,
  visibleMechanicCount: number,
): CalendarDensity {
  if (stored === 'comfortable' || stored === 'compact') return stored;
  return visibleMechanicCount > MANY_MECHANICS_THRESHOLD ? 'compact' : 'comfortable';
}

export function resolveSelectedMechanicIds(
  persisted: string[] | null,
  mechanics: ReadonlyArray<{ id: string; isAvailable: boolean }>,
): { ids: string[]; shouldPersist: boolean } {
  if (mechanics.length === 0) {
    return { ids: persisted ?? [], shouldPersist: false };
  }
  if (persisted === null) {
    return {
      ids: mechanics.filter((m) => m.isAvailable).map((m) => m.id),
      shouldPersist: true,
    };
  }
  const known = new Set(mechanics.map((m) => m.id));
  const ids = persisted.filter((id) => known.has(id));
  if (persisted.length > 0 && ids.length === 0) {
    return {
      ids: mechanics.filter((m) => m.isAvailable).map((m) => m.id),
      shouldPersist: true,
    };
  }
  if (ids.length !== persisted.length) {
    return { ids, shouldPersist: true };
  }
  return { ids, shouldPersist: false };
}

export interface PersistedMechanicPrefs {
  selectedMechanicIds: string[] | null;
  groups: MechanicGroup[];
  density: CalendarDensity | null;
  dayLayoutTouched: boolean;
  /** When true, mechanics absent for the whole visible range are hidden from columns (#2902). */
  hideAbsentMechanics: boolean;
}

const defaultPrefs: PersistedMechanicPrefs = {
  selectedMechanicIds: null,
  groups: [],
  density: null,
  dayLayoutTouched: false,
  hideAbsentMechanics: true,
};

export function loadPersistedMechanicPrefs(): PersistedMechanicPrefs {
  try {
    const raw = localStorage.getItem(CALENDAR_MECHANIC_PREFS_STORAGE_KEY);
    if (!raw) return { ...defaultPrefs };
    const parsed = JSON.parse(raw) as { state?: Partial<PersistedMechanicPrefs> } & Partial<PersistedMechanicPrefs>;
    const state = parsed.state ?? parsed;
    const density = state.density === 'compact' || state.density === 'comfortable' ? state.density : null;
    return {
      selectedMechanicIds: Array.isArray(state.selectedMechanicIds) ? state.selectedMechanicIds : null,
      groups: Array.isArray(state.groups) ? state.groups : [],
      density,
      dayLayoutTouched: state.dayLayoutTouched === true,
      hideAbsentMechanics: state.hideAbsentMechanics !== false,
    };
  } catch {
    return { ...defaultPrefs };
  }
}

function persistMechanicPrefs(state: PersistedMechanicPrefs): void {
  try {
    localStorage.setItem(
      CALENDAR_MECHANIC_PREFS_STORAGE_KEY,
      JSON.stringify({ state }),
    );
  } catch {
    // Ignore quota / private-mode failures — the in-memory prefs still work.
  }
}

function snapshotPrefs(state: PersistedMechanicPrefs): PersistedMechanicPrefs {
  return {
    selectedMechanicIds: state.selectedMechanicIds,
    groups: state.groups,
    density: state.density,
    dayLayoutTouched: state.dayLayoutTouched,
    hideAbsentMechanics: state.hideAbsentMechanics,
  };
}

interface CalendarMechanicPrefsState extends PersistedMechanicPrefs {
  setSelectedMechanicIds: (ids: string[]) => void;
  setDensity: (density: CalendarDensity) => void;
  markDayLayoutTouched: () => void;
  setHideAbsentMechanics: (hide: boolean) => void;
  saveGroup: (name: string, mechanicIds: string[]) => MechanicGroup | null;
  deleteGroup: (id: string) => void;
  applyGroup: (id: string) => string[] | null;
  hydrateFromStorage: () => void;
  reset: () => void;
}

export const useCalendarMechanicPrefsStore = create<CalendarMechanicPrefsState>()((set, get) => {
  const persistGet = () => persistMechanicPrefs(snapshotPrefs(get()));
  return {
    ...loadPersistedMechanicPrefs(),
    setSelectedMechanicIds: (ids) => {
      set({ selectedMechanicIds: ids });
      persistGet();
    },
    setDensity: (density) => {
      set({ density });
      persistGet();
    },
    markDayLayoutTouched: () => {
      set({ dayLayoutTouched: true });
      persistGet();
    },
    setHideAbsentMechanics: (hideAbsentMechanics) => {
      set({ hideAbsentMechanics });
      persistGet();
    },
    saveGroup: (name, mechanicIds) => {
      const group = createMechanicGroup(name, mechanicIds);
      if (!group) return null;
      set({ groups: [...get().groups, group] });
      persistGet();
      return group;
    },
    deleteGroup: (id) => {
      set({ groups: get().groups.filter((g) => g.id !== id) });
      persistGet();
    },
    applyGroup: (id) => {
      const group = get().groups.find((g) => g.id === id);
      if (!group) return null;
      set({ selectedMechanicIds: [...group.mechanicIds] });
      persistGet();
      return group.mechanicIds;
    },
    hydrateFromStorage: () => {
      set(loadPersistedMechanicPrefs());
    },
    reset: () => {
      set({ ...defaultPrefs });
      persistMechanicPrefs({ ...defaultPrefs });
    },
  };
});
